'use client';
import React, { useState, useCallback } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from '@stripe/react-stripe-js';
import type { StripePaymentElementOptions } from '@stripe/stripe-js';
import { ChevronLeft, Lock, Loader2, AlertCircle, Check, CreditCard } from 'lucide-react';
import type { BookingState } from './BookingWizard';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';

interface Props {
  booking: BookingState;
  onComplete: (paymentType: 'deposit' | 'full', bookingId: string) => void;
  onBack: () => void;
  onPaymentTypeChange: (t: 'deposit' | 'full') => void;
}

// ─── Inner form (rendered inside <Elements>) ────────────────────────────────
interface PaymentFormProps {
  dueNow: number;
  paymentType: 'deposit' | 'full';
  onBack: () => void;
  onSuccess: (bookingId: string) => void;
  paymentIntentId: string;
}

function StripePaymentForm({ dueNow, paymentType, onBack, onSuccess, paymentIntentId }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMsg(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/booking-flow?payment=success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMsg(error.message ?? 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        const supabase = createClient();
        const { data, error: confirmError } = await supabase.functions.invoke<{
          success: boolean;
          bookingId: string;
        }>('confirm-payment', {
          body: { paymentIntentId: paymentIntent.id },
        });

        if (confirmError) {
          console.error('Confirm payment error:', confirmError);
        }

        onSuccess(data?.bookingId ?? `DB-${paymentIntent.id.slice(-8).toUpperCase()}`);
      } catch (err) {
        console.error('Confirm payment failed:', err);
        onSuccess(`DB-${paymentIntent.id.slice(-8).toUpperCase()}`);
      }
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-primary" />
            <h3 className="text-sm font-bold text-foreground">Card Details</h3>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock size={12} />
            256-bit SSL
          </div>
        </div>

        <PaymentElement
          options={{
            layout: 'tabs',
          } as StripePaymentElementOptions}
        />

        {errorMsg && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-300">{errorMsg}</p>
          </div>
        )}

        <div className="flex items-center gap-4 pt-2">
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            className="flex items-center gap-2 px-5 py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
            Back
          </button>
          <button
            type="submit"
            disabled={!stripe || isProcessing}
            className="flex-1 flex items-center justify-center gap-2 px-8 py-3 rounded-xl gold-gradient text-primary-foreground text-base font-bold transition-all duration-150 hover:opacity-90 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed shadow-gold"
          >
            {isProcessing ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Processing Payment...
              </>
            ) : (
              <>
                <Lock size={16} />
                Pay ${dueNow.toLocaleString()} &amp; Confirm Booking
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

// ─── Main StepPayment component ──────────────────────────────────────────────
export default function StepPayment({ booking, onComplete, onBack, onPaymentTypeChange }: Props) {
  const [paymentType, setPaymentType] = useState<'deposit' | 'full'>(booking.paymentType);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string>('');
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);

  const { service, clientDetails, dateTime } = booking;
  const addonsTotal = service?.addons.reduce((sum, a) => sum + a.price, 0) ?? 0;
  const subtotal = (service?.price ?? 0) + addonsTotal;
  const depositAmount = Math.round(subtotal * 0.3);
  const dueNow = paymentType === 'deposit' ? depositAmount : subtotal;

  const handleTypeChange = (t: 'deposit' | 'full') => {
    setPaymentType(t);
    onPaymentTypeChange(t);
    // Reset intent so it gets recreated with new amount
    setClientSecret(null);
    setPaymentIntentId('');
    setIntentError(null);
  };

  const createPaymentIntent = useCallback(async () => {
    if (!clientDetails || !service || !dateTime) return;
    setIsCreatingIntent(true);
    setIntentError(null);

    try {
      const supabase = createClient();
      const { data, error } = await supabase.functions.invoke<{
        clientSecret: string;
        paymentIntentId: string;
        bookingId: string | null;
      }>('create-payment-intent', {
        body: {
          amount: dueNow,
          paymentType,
          customerInfo: {
            firstName: clientDetails.firstName,
            lastName: clientDetails.lastName,
            email: clientDetails.email,
            phone: clientDetails.phone,
          },
          bookingDetails: {
            serviceName: service.name,
            vehicleYear: clientDetails.vehicleYear,
            vehicleMake: clientDetails.vehicleMake,
            vehicleModel: clientDetails.vehicleModel,
            date: dateTime.date,
            time: dateTime.time,
            addons: service.addons.map((a) => a.name),
            notes: clientDetails.notes,
          },
          userId: null,
        },
      });

      if (error) {
        const msg = (data as { error?: string })?.error ?? error.message ?? 'Failed to set up payment';
        throw new Error(msg);
      }

      if (!data?.clientSecret) throw new Error('No client secret returned');

      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId ?? '');
    } catch (err) {
      console.error('Create payment intent failed:', err);
      setIntentError(err instanceof Error ? err.message : 'Failed to set up payment. Please try again.');
    } finally {
      setIsCreatingIntent(false);
    }
  }, [dueNow, paymentType, clientDetails, service, dateTime]);

  const stripePromise = getStripe();

  const stripeAppearance = {
    theme: 'night' as const,
    variables: {
      colorPrimary: '#D4AF37',
      colorBackground: '#1a1a1a',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'inherit',
      borderRadius: '8px',
    },
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-foreground mb-2">Payment</h1>
        <p className="text-muted-foreground">Secure your booking with a deposit or pay the full amount now.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Payment form */}
        <div className="lg:col-span-3 space-y-5">
          {/* Payment type toggle */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-4">Payment Option</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleTypeChange('deposit')}
                className={`p-4 rounded-xl border text-left transition-all duration-200 ${
                  paymentType === 'deposit' ? 'border-primary bg-primary/5 shadow-gold' : 'border-border hover:border-border/80'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border-2 mb-3 flex items-center justify-center ${
                  paymentType === 'deposit' ? 'border-primary' : 'border-muted-foreground'
                }`}>
                  {paymentType === 'deposit' && <div className="w-2 h-2 rounded-full bg-primary" />}
                </div>
                <p className="text-sm font-bold text-foreground">30% Deposit</p>
                <p className="text-xl font-extrabold text-primary tabular-nums mt-1">${depositAmount.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">Remaining ${(subtotal - depositAmount).toLocaleString()} due on the day</p>
              </button>

              <button
                type="button"
                onClick={() => handleTypeChange('full')}
                className={`p-4 rounded-xl border text-left transition-all duration-200 ${
                  paymentType === 'full' ? 'border-primary bg-primary/5 shadow-gold' : 'border-border hover:border-border/80'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border-2 mb-3 flex items-center justify-center ${
                  paymentType === 'full' ? 'border-primary' : 'border-muted-foreground'
                }`}>
                  {paymentType === 'full' && <div className="w-2 h-2 rounded-full bg-primary" />}
                </div>
                <p className="text-sm font-bold text-foreground">Pay in Full</p>
                <p className="text-xl font-extrabold text-primary tabular-nums mt-1">${subtotal.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">Nothing due on appointment day</p>
              </button>
            </div>
          </div>

          {/* Stripe Payment Form */}
          {!clientSecret ? (
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <CreditCard size={16} className="text-primary" />
                  <h3 className="text-sm font-bold text-foreground">Card Details</h3>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Lock size={12} />
                  256-bit SSL
                </div>
              </div>

              {intentError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-300">{intentError}</p>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                Click below to securely load the payment form powered by Stripe.
              </p>

              <div className="flex items-center gap-4 pt-2">
                <button
                  type="button"
                  onClick={onBack}
                  disabled={isCreatingIntent}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
                <button
                  type="button"
                  onClick={createPaymentIntent}
                  disabled={isCreatingIntent}
                  className="flex-1 flex items-center justify-center gap-2 px-8 py-3 rounded-xl gold-gradient text-primary-foreground text-base font-bold transition-all duration-150 hover:opacity-90 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed shadow-gold"
                >
                  {isCreatingIntent ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Setting up payment...
                    </>
                  ) : (
                    <>
                      <Lock size={16} />
                      Proceed to Pay ${dueNow.toLocaleString()}
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: stripeAppearance }}
            >
              <StripePaymentForm
                dueNow={dueNow}
                paymentType={paymentType}
                onBack={() => setClientSecret(null)}
                onSuccess={(bookingId) => onComplete(paymentType, bookingId)}
                paymentIntentId={paymentIntentId}
              />
            </Elements>
          )}
        </div>

        {/* Order summary */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-xl p-5 sticky top-24">
            <h3 className="text-sm font-bold text-foreground mb-4">Order Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-2">
                <div>
                  <span className="text-muted-foreground">{service?.name}</span>
                  {service?.vehicleSize && (
                    <p className="text-xs text-muted-foreground/70 mt-0.5">{service.vehicleSize}</p>
                  )}
                </div>
                <span className="text-foreground tabular-nums whitespace-nowrap">
                  {service?.priceLabel || (service?.price ? `$${service.price}` : '—')}
                </span>
              </div>
              {service?.addons.map((a) => (
                <div key={`pay-addon-${a.id}`} className="flex justify-between">
                  <span className="text-muted-foreground">{a.name}</span>
                  <span className="text-foreground tabular-nums">+{a.priceLabel || `$${a.price}`}</span>
                </div>
              ))}
              <div className="pt-3 border-t border-border flex justify-between font-semibold">
                <span className="text-muted-foreground">Subtotal (est.)</span>
                <span className="text-foreground tabular-nums">${subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{paymentType === 'deposit' ? 'Remaining balance (due at appointment)' : 'Balance remaining'}</span>
                <span className="tabular-nums">${paymentType === 'deposit' ? (subtotal - depositAmount).toLocaleString() : 0}</span>
              </div>
              <div className="pt-3 border-t border-border flex justify-between items-center">
                <span className="text-base font-bold text-foreground">Due Now</span>
                <span className="text-2xl font-extrabold text-primary tabular-nums">${dueNow.toLocaleString()}</span>
              </div>
            </div>

            {/* What happens next */}
            <div className="mt-5 pt-5 border-t border-border space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">What happens next</p>
              {[
                { text: 'Instant confirmation email' },
                { text: 'SMS confirmation to your phone' },
                { text: '24-hour reminder before appointment' },
              ].map((item) => (
                <div key={`next-${item.text}`} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Check size={10} className="text-emerald-400" />
                  </div>
                  <span className="text-xs text-muted-foreground">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}